import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllSubDCA, getItCodesLookup, getApprovalStatusSDCA, updateSubDCA } from '../../api/AccountsAPI/subDcaAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const fetchSubDCAGrid = createAsyncThunk('subDca/fetchGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllSubDCA(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub account heads')); }
});

export const fetchItCodesLookup = createAsyncThunk('subDca/fetchItCodes', async (_, { rejectWithValue }) => {
    try { const res = await getItCodesLookup(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch IT codes')); }
});

export const fetchApprovalStatusSDCA = createAsyncThunk('subDca/fetchApprovalStatus', async (roleId, { rejectWithValue }) => {
    try { const res = await getApprovalStatusSDCA('SubAccount Head', roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch allowed actions')); }
});

export const submitUpdateSubDCA = createAsyncThunk('subDca/submitUpdate', async (payload, { rejectWithValue }) => {
    try {
        const res = await updateSubDCA(payload);
        const status = res?.Data;
        if (status !== 'Updated') return rejectWithValue(status || 'Failed to update sub account head');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update sub account head')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    grid: [], gridLoading: false, gridError: null,
    itCodes: [], itCodesLoading: false,
    allowedActions: [], allowedActionsLoading: false,

    updateLoading: false, updateResult: null, updateError: null,
};

const subDcaSlice = createSlice({
    name: 'subDca',
    initialState,
    reducers: {
        clearUpdateResult: (s) => { s.updateResult = null; s.updateError = null; },
        resetSubDca: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubDCAGrid.pending, (s) => { s.gridLoading = true; s.gridError = null; })
            .addCase(fetchSubDCAGrid.fulfilled, (s, a) => { s.gridLoading = false; s.grid = a.payload; })
            .addCase(fetchSubDCAGrid.rejected, (s, a) => { s.gridLoading = false; s.gridError = a.payload; });

        builder
            .addCase(fetchItCodesLookup.pending, (s) => { s.itCodesLoading = true; })
            .addCase(fetchItCodesLookup.fulfilled, (s, a) => { s.itCodesLoading = false; s.itCodes = a.payload; })
            .addCase(fetchItCodesLookup.rejected, (s) => { s.itCodesLoading = false; });

        builder
            .addCase(fetchApprovalStatusSDCA.pending, (s) => { s.allowedActionsLoading = true; })
            .addCase(fetchApprovalStatusSDCA.fulfilled, (s, a) => { s.allowedActionsLoading = false; s.allowedActions = a.payload; })
            .addCase(fetchApprovalStatusSDCA.rejected, (s) => { s.allowedActionsLoading = false; });

        builder
            .addCase(submitUpdateSubDCA.pending, (s) => { s.updateLoading = true; s.updateError = null; s.updateResult = null; })
            .addCase(submitUpdateSubDCA.fulfilled, (s, a) => { s.updateLoading = false; s.updateResult = a.payload; })
            .addCase(submitUpdateSubDCA.rejected, (s, a) => { s.updateLoading = false; s.updateError = a.payload; });
    },
});

export const { clearUpdateResult, resetSubDca } = subDcaSlice.actions;

export const selectSubDca = (s) => s.subDca;

export default subDcaSlice.reducer;
